import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Shared admin modal shell. Visual copy of the SEO modal chrome,
 * with separate class names so SEO screens stay untouched.
 *
 * Usage:
 *   <UiModal
 *     isOpen={open}
 *     onClose={close}
 *     eyebrow="Categories"
 *     title="Add Category"
 *     as="form"
 *     onSubmit={handleSubmit}
 *     footer={
 *       <>
 *         <button type="button" className="ui-btn-cancel" onClick={close}>Cancel</button>
 *         <button type="submit" className="ui-btn-save">Save</button>
 *       </>
 *     }
 *   >
 *     <section className="ui-modal-section">...</section>
 *   </UiModal>
 */
export function UiModal({
  isOpen,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  as = "div",
  onSubmit,
  panelClassName = "",
  bodyClassName = "",
}) {
  if (!isOpen) return null;

  const bodyAndFooter = (
    <>
      <div className={`ui-modal-body ${bodyClassName}`.trim()}>{children}</div>
      {footer ? <div className="ui-modal-footer">{footer}</div> : null}
    </>
  );

  const content =
    as === "form" ? (
      <form
        onSubmit={onSubmit}
        className="flex min-h-0 flex-1 flex-col"
        noValidate
      >
        {bodyAndFooter}
      </form>
    ) : (
      <div className="flex min-h-0 flex-1 flex-col">{bodyAndFooter}</div>
    );

  return createPortal(
    <div className="ui-modal-overlay" role="dialog" aria-modal="true">
      <div className={`ui-modal-panel ${panelClassName}`.trim()}>
        <div className="ui-modal-header brand-gradient">
          <div>
            {eyebrow ? <p className="ui-modal-eyebrow">{eyebrow}</p> : null}
            {title ? <h3 className="ui-modal-title">{title}</h3> : null}
          </div>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="ui-modal-close"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          ) : null}
        </div>
        {content}
      </div>
    </div>,
    document.body,
  );
}

export default UiModal;
